import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import Header from '../../components/ui/Header';
import FilterSection from './components/FilterSection';
import BrandModelFilter from './components/BrandModelFilter';
import CategoryFilter from './components/CategoryFilter';
import PriceRangeFilter from './components/PriceRangeFilter';
import LocationFilter from './components/LocationFilter';
import SavedSearches from './components/SavedSearches';
import FilterSummary from './components/FilterSummary';

const AdvancedSearch = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [isSearching, setIsSearching] = useState(false);
  const [resultCount, setResultCount] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    brand: '',
    model: '',
    categories: [],
    condition: '',
    minPrice: '',
    maxPrice: '',
    yearMin: '',
    yearMax: '',
    region: '',
    department: '',
    searchRadius: '25',
    sellerType: '',
    listingAge: '',
    availability: 'available',
    useCurrentLocation: false
  });

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }

    // Load filters from URL params
    const urlFilters = {};
    searchParams?.forEach((value, key) => {
      if (key === 'categories') {
        urlFilters[key] = value?.split(',');
      } else {
        urlFilters[key] = value;
      }
    });
    
    if (Object.keys(urlFilters)?.length > 0) {
      setFilters(prev => ({ ...prev, ...urlFilters }));
    }
  }, [searchParams]);

  const conditionOptions = [
    { value: 'new', label: currentLanguage === 'fr' ? 'Neuf' : 'New' },
    { value: 'used', label: currentLanguage === 'fr' ? 'Occasion' : 'Used' },
    { value: 'refurbished', label: currentLanguage === 'fr' ? 'Reconditionné' : 'Refurbished' },
    { value: 'parts', label: currentLanguage === 'fr' ? 'Pour pièces' : 'For parts' }
  ];

  const sellerTypeOptions = [
    { value: 'individual', label: currentLanguage === 'fr' ? 'Particulier' : 'Individual' },
    { value: 'professional', label: currentLanguage === 'fr' ? 'Professionnel' : 'Professional' },
    { value: 'dealer', label: currentLanguage === 'fr' ? 'Concessionnaire' : 'Dealer' }
  ];

  const listingAgeOptions = [
    { value: '1', label: currentLanguage === 'fr' ? 'Moins de 24h' : 'Less than 24h' },
    { value: '7', label: currentLanguage === 'fr' ? 'Moins d\'une semaine' : 'Less than a week' },
    { value: '30', label: currentLanguage === 'fr' ? 'Moins d\'un mois' : 'Less than a month' },
    { value: '90', label: currentLanguage === 'fr' ? 'Moins de 3 mois' : 'Less than 3 months' }
  ];

  const availabilityOptions = [
    { value: 'available', label: currentLanguage === 'fr' ? 'Disponible' : 'Available' },
    { value: 'reserved', label: currentLanguage === 'fr' ? 'Réservé' : 'Reserved' },
    { value: 'sold', label: currentLanguage === 'fr' ? 'Vendu' : 'Sold' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handlePriceChange = (minPrice, maxPrice) => {
    setFilters(prev => ({ ...prev, minPrice, maxPrice }));
  };

  const handleYearChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = async () => {
    setIsSearching(true);
    
    // Simulate search API call
    setTimeout(() => {
      const mockResultCount = Math.floor(Math.random() * 1000) + 50;
      setResultCount(mockResultCount);
      
      // Build search URL with filters
      const searchUrl = new URLSearchParams();
      Object.entries(filters)?.forEach(([key, value]) => {
        if (value && (Array.isArray(value) ? value?.length > 0 : true)) {
          searchUrl?.set(key, Array.isArray(value) ? value?.join(',') : value);
        }
      });
      
      navigate(`/search-results?${searchUrl?.toString()}`);
      setIsSearching(false);
    }, 1500);
  };

  const handleClearAllFilters = () => {
    setFilters({
      brand: '',
      model: '',
      categories: [],
      condition: '',
      minPrice: '',
      maxPrice: '',
      yearMin: '',
      yearMax: '',
      region: '',
      department: '',
      searchRadius: '25',
      sellerType: '',
      listingAge: '',
      availability: 'available',
      useCurrentLocation: false
    });
    setResultCount(null);
  };

  const handleRemoveFilter = (filterKey) => {
    if (filterKey === 'price') {
      setFilters(prev => ({ ...prev, minPrice: '', maxPrice: '' }));
    } else if (filterKey === 'year') {
      setFilters(prev => ({ ...prev, yearMin: '', yearMax: '' }));
    } else if (filterKey === 'categories') {
      setFilters(prev => ({ ...prev, categories: [] }));
    } else {
      setFilters(prev => ({ ...prev, [filterKey]: '' }));
    }
  };

  const handleLoadSavedSearch = (savedFilters) => {
    setFilters(savedFilters);
  };

  const hasActiveFilters = () => {
    return Object.entries(filters)?.some(([key, value]) => {
      if (key === 'searchRadius' || key === 'availability') return false;
      return Array.isArray(value) ? value?.length > 0 : value !== '';
    });
  };

  return (
    <>
      <Helmet>
        <title>Recherche Avancée - AutoCasse | Advanced Search</title>
        <meta name="description" content="Recherche avancée de pièces automobiles et véhicules au Cameroun. Filtres par marque, modèle, prix, localisation et plus." />
        <meta name="keywords" content="recherche avancée, pièces auto, véhicules, Cameroun, filtres" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-4 py-6 pb-20 md:pb-6">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/homepage-dashboard')}
                iconName="ArrowLeft"
                iconSize={16}
              />
              <h1 className="text-2xl font-bold text-foreground">
                {currentLanguage === 'fr' ? 'Recherche Avancée' : 'Advanced Search'}
              </h1>
            </div>
            <p className="text-muted-foreground">
              {currentLanguage === 'fr' ?'Utilisez les filtres ci-dessous pour trouver exactement ce que vous cherchez' :'Use the filters below to find exactly what you\'re looking for'
              }
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Filters Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Filter Summary */}
              {hasActiveFilters() && (
                <FilterSummary
                  filters={filters}
                  onClearAll={handleClearAllFilters}
                  onRemoveFilter={handleRemoveFilter}
                  resultCount={resultCount}
                />
              )}

              {/* Brand & Model Filter */}
              <FilterSection
                title={currentLanguage === 'fr' ? 'Marque et Modèle' : 'Brand & Model'}
                icon="Car"
                defaultExpanded={true}
              >
                <BrandModelFilter
                  selectedBrand={filters?.brand}
                  selectedModel={filters?.model}
                  onBrandChange={(value) => handleFilterChange('brand', value)}
                  onModelChange={(value) => handleFilterChange('model', value)}
                />
              </FilterSection>

              {/* Category Filter */}
              <FilterSection
                title={currentLanguage === 'fr' ? 'Catégories' : 'Categories'}
                icon="Grid3X3"
                defaultExpanded={true}
              >
                <CategoryFilter
                  selectedCategories={filters?.categories}
                  onCategoryChange={(value) => handleFilterChange('categories', value)}
                />
              </FilterSection>

              {/* Condition & Details */}
              <FilterSection
                title={currentLanguage === 'fr' ? 'État et Détails' : 'Condition & Details'}
                icon="Settings"
                defaultExpanded={true}
              >
                <div className="space-y-4">
                  <Select
                    label={currentLanguage === 'fr' ? 'État' : 'Condition'}
                    placeholder={currentLanguage === 'fr' ? 'Sélectionner l\'état' : 'Select condition'}
                    options={conditionOptions}
                    value={filters?.condition}
                    onChange={(value) => handleFilterChange('condition', value)}
                    clearable
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      label={currentLanguage === 'fr' ? 'Année min' : 'Year from'}
                      placeholder="1990"
                      options={Array.from({ length: 35 }, (_, i) => ({
                        value: (1990 + i)?.toString(),
                        label: (1990 + i)?.toString()
                      }))}
                      value={filters?.yearMin}
                      onChange={(value) => handleYearChange('yearMin', value)}
                      searchable
                      clearable
                    />
                    <Select
                      label={currentLanguage === 'fr' ? 'Année max' : 'Year to'}
                      placeholder={new Date()?.getFullYear()?.toString()}
                      options={Array.from({ length: 35 }, (_, i) => ({
                        value: (1990 + i)?.toString(),
                        label: (1990 + i)?.toString()
                      }))}
                      value={filters?.yearMax}
                      onChange={(value) => handleYearChange('yearMax', value)}
                      searchable
                      clearable
                    />
                  </div>

                  <Select
                    label={currentLanguage === 'fr' ? 'Type de vendeur' : 'Seller type'}
                    placeholder={currentLanguage === 'fr' ? 'Tous les vendeurs' : 'All sellers'}
                    options={sellerTypeOptions}
                    value={filters?.sellerType}
                    onChange={(value) => handleFilterChange('sellerType', value)}
                    clearable
                  />
                </div>
              </FilterSection>

              {/* Price Range */}
              <FilterSection
                title={currentLanguage === 'fr' ? 'Gamme de Prix' : 'Price Range'}
                icon="DollarSign"
                defaultExpanded={true}
              >
                <PriceRangeFilter
                  minPrice={filters?.minPrice}
                  maxPrice={filters?.maxPrice}
                  onPriceChange={handlePriceChange}
                />
              </FilterSection>

              {/* Location Filter */}
              <FilterSection
                title={currentLanguage === 'fr' ? 'Localisation' : 'Location'}
                icon="MapPin"
                defaultExpanded={true}
              >
                <LocationFilter
                  selectedRegion={filters?.region}
                  selectedDepartment={filters?.department}
                  searchRadius={filters?.searchRadius}
                  useCurrentLocation={filters?.useCurrentLocation}
                  onRegionChange={(value) => handleFilterChange('region', value)}
                  onDepartmentChange={(value) => handleFilterChange('department', value)}
                  onRadiusChange={(value) => handleFilterChange('searchRadius', value)}
                  onLocationToggle={(value) => handleFilterChange('useCurrentLocation', value)}
                />
              </FilterSection>

              {/* Additional Filters */}
              <FilterSection
                title={currentLanguage === 'fr' ? 'Filtres Supplémentaires' : 'Additional Filters'}
                icon="SlidersHorizontal"
                defaultExpanded={false}
              >
                <div className="space-y-4">
                  <Select
                    label={currentLanguage === 'fr' ? 'Âge de l\'annonce' : 'Listing age'}
                    placeholder={currentLanguage === 'fr' ? 'Toutes les annonces' : 'All listings'}
                    options={listingAgeOptions}
                    value={filters?.listingAge}
                    onChange={(value) => handleFilterChange('listingAge', value)}
                    clearable
                  />

                  <Select
                    label={currentLanguage === 'fr' ? 'Disponibilité' : 'Availability'}
                    placeholder={currentLanguage === 'fr' ? 'Sélectionner' : 'Select'}
                    options={availabilityOptions}
                    value={filters?.availability}
                    onChange={(value) => handleFilterChange('availability', value)}
                  />

                  <div className="space-y-2">
                    <Checkbox
                      label={currentLanguage === 'fr' ? 'Avec photos uniquement' : 'With photos only'}
                      checked={filters?.withPhotos || false}
                      onChange={(e) => handleFilterChange('withPhotos', e?.target?.checked)}
                    />
                    <Checkbox
                      label={currentLanguage === 'fr' ? 'Livraison disponible' : 'Delivery available'}
                      checked={filters?.deliveryAvailable || false}
                      onChange={(e) => handleFilterChange('deliveryAvailable', e?.target?.checked)}
                    />
                    <Checkbox
                      label={currentLanguage === 'fr' ? 'Vendeurs vérifiés uniquement' : 'Verified sellers only'}
                      checked={filters?.verifiedSellers || false}
                      onChange={(e) => handleFilterChange('verifiedSellers', e?.target?.checked)}
                    />
                  </div>
                </div>
              </FilterSection>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Search Action */}
              <div className="bg-card border border-border rounded-lg p-4 sticky top-20">
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleSearch}
                  loading={isSearching}
                  iconName="Search"
                  iconPosition="left"
                  iconSize={16}
                  className="mb-3"
                >
                  {isSearching 
                    ? (currentLanguage === 'fr' ? 'Recherche...' : 'Searching...') 
                    : (currentLanguage === 'fr' ? 'Lancer la recherche' : 'Start search')
                  }
                </Button>

                {resultCount !== null && (
                  <div className="text-center text-sm text-muted-foreground">
                    {resultCount?.toLocaleString('fr-FR')} {currentLanguage === 'fr' ? 'résultats trouvés' : 'results found'}
                  </div>
                )}

                {hasActiveFilters() && (
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleClearAllFilters}
                    iconName="X"
                    iconPosition="left"
                    iconSize={14}
                    className="mt-2"
                  >
                    {currentLanguage === 'fr' ? 'Effacer tout' : 'Clear all'}
                  </Button>
                )}
              </div>

              {/* Saved Searches */}
              <SavedSearches
                currentFilters={filters}
                onLoadSearch={handleLoadSavedSearch}
              />

              {/* Search Tips */}
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Icon name="Lightbulb" size={16} className="text-accent mt-0.5" />
                  <div className="text-sm">
                    <h4 className="font-medium text-foreground mb-2">
                      {currentLanguage === 'fr' ? 'Conseils de recherche' : 'Search tips'}
                    </h4>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• {currentLanguage === 'fr' ? 'Utilisez moins de filtres pour plus de résultats' : 'Use fewer filters for more results'}</li>
                      <li>• {currentLanguage === 'fr' ? 'Sauvegardez vos recherches fréquentes' : 'Save your frequent searches'}</li>
                      <li>• {currentLanguage === 'fr' ? 'Activez les alertes pour les nouvelles annonces' : 'Enable alerts for new listings'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdvancedSearch;